<template>
	<div
		v-if="group.hasVisibleHeader && !group.isUnassigned"
		:ref="measureElement"
		:data-index="group.headerIndex"
		:style="group.headerStyle"
	>
		<SuiSelectListHelpers
			class="user-assign-select-inline-list-group__wrapper"
			:data-role-id="group.role?.id"
			@click="handleGroupClick"
		>
			<SuiSelectListHelpersSelectAll
				v-if="!isSingleUserAssign && !isRoleFilterActive"
				:checked="group.isAllUsersSelected && !group.isRoleSelectable"
				select-all-text=""
				:aria-label="
					$t('user_assign_select_all_by_group_label', {
						name: group.name,
					})
				"
				@input="$emit('toggle-all-users-selected', group, $event)"
			/>
			<h3 class="user-assign-select-inline-list-group__name">
				{{ group.name }}
			</h3>
			<SuiIcon
				name="icon-arrow-down-16"
				class="user-assign-select-inline-list-group__expand-icon"
				:class="{
					'user-assign-select-inline-list-group__expand-icon--rotate':
						isClosed,
				}"
			/>
		</SuiSelectListHelpers>
	</div>

	<div
		v-if="group.children && group.children.length > 0"
		role="group"
		:aria-label="group.name"
	>
		<template
			v-for="{ data: item, index, style } in group.children"
			:key="index"
		>
			<div :ref="measureElement" :data-index="index" :style="style">
				<UserAssignSelectListRoleItem
					v-if="item.type === VirtualItemType.Role"
					:role="item.role"
					:is-selected="isRoleSelected(item.role)"
					:data-index="index"
					@click="$emit('role-clicked', $event)"
				/>
				<UserAssignSelectListUserItem
					v-else-if="item.type === VirtualItemType.User"
					:user="item.user"
					:is-selected="selectedUsers.includes(item.user.id)"
					:task-users-availability="taskUsersAvailability"
					:group-id="item.group.id"
					:data-index="index"
					@click="$emit('user-clicked', $event)"
				/>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
	SuiSelectListHelpers,
	SuiSelectListHelpersSelectAll,
	SuiIcon,
} from '@developers/scoro-ui';
import {
	type User,
	type UserAssignSelectInlineAvailabilityQuery,
} from '@/generated/graphql';
import {
	UserAssignSelectListUserItem,
	UserAssignSelectListRoleItem,
} from '../../UserAssignSelect/List';
import {
	VirtualItemType,
	type UserAssignGroup,
	type UserAssignSelectRole,
} from '../../../types';

export interface UserAssignSelectInlineListGroupProps {
	group: UserAssignGroup;
	isClosed: boolean;
	selectedUsers: Array<User['id']>;
	selectedRoles: UserAssignSelectRole[];
	isSingleUserAssign: boolean;
	taskUsersAvailability: UserAssignSelectInlineAvailabilityQuery['TaskUsersAvailability'];
	activeRoleFilterId: UserAssignSelectRole['id'] | null;
	measureElement: (el: unknown) => void;
}

const { group, selectedRoles, activeRoleFilterId, measureElement } =
	defineProps<UserAssignSelectInlineListGroupProps>();

const emit = defineEmits<{
	'toggle-group-dropdown': [target: HTMLElement, group: UserAssignGroup];
	'toggle-all-users-selected': [group: UserAssignGroup, value: boolean];
	'user-clicked': [userId: User['id']];
	'role-clicked': [role: UserAssignSelectRole];
}>();

const isRoleSelected = (role: UserAssignSelectRole) => {
	return selectedRoles.some((r) => r?.id === role.id);
};

const isRoleFilterActive = computed(() => {
	return activeRoleFilterId !== null;
});

const handleGroupClick = (event: MouseEvent) => {
	emit('toggle-group-dropdown', event.target as HTMLElement, group);
};
</script>

<style scoped lang="scss">
.user-assign-select-inline-list-group {
	&__wrapper {
		display: flex;
		justify-content: flex-start;
		padding: 1.5rem 2rem;

		&:hover {
			cursor: pointer;
		}
	}

	&__name {
		font-size: 1.625rem;
		font-weight: $scoro-fw-semi-bold;
	}

	&__expand-icon {
		margin-left: auto;
		transition: transform 0.2s ease;

		&--rotate {
			transform: rotate(-90deg);
		}
	}
}
</style>
